/**
 * Safe inquiry context when navigating from /bookings to listing detail.
 */

import { shortId } from './bookingInquiryDisplay';

export const INQUIRY_QUERY_PARAM = 'inquiry';
export const FOCUS_QUERY_PARAM = 'focus';
export const RETURN_TO_QUERY_PARAM = 'returnTo';
export const RETURN_TO_BOOKINGS = 'bookings';

const FORBIDDEN_CONTEXT_KEYS = new Set([
  'message',
  'special_requests',
  'owner_response',
  'guest_email',
  'owner_email',
  'email',
  'phone',
  'phone_number',
  'address',
  'notes_internal',
]);

/**
 * Stable DOM id for an inquiry card on /bookings (safe for getElementById).
 */
export function inquiryCardDomId(inquiryId) {
  if (!inquiryId) {
    return undefined;
  }
  const safe = String(inquiryId).replace(/[^a-zA-Z0-9_-]/g, '');
  return safe ? `inquiry-${safe}` : undefined;
}

/**
 * Return URL for Back to My Bookings with scroll restoration focus.
 */
export function buildBookingsReturnUrl(inquiryId) {
  if (!inquiryId) {
    return '/bookings';
  }
  const params = new URLSearchParams();
  params.set(FOCUS_QUERY_PARAM, String(inquiryId).trim());
  return `/bookings?${params.toString()}`;
}

/**
 * Build listing detail URL with inquiry + return context (refresh-safe query params).
 */
export function buildListingDetailUrlFromInquiry(inquiry) {
  const listingId = inquiry?.listing_id;
  if (!listingId) {
    return '/listings';
  }
  const base = `/listings/${listingId}`;
  const inquiryId = inquiry?.id || inquiry?.inquiry_id;
  if (!inquiryId) {
    return base;
  }
  const params = new URLSearchParams();
  params.set(INQUIRY_QUERY_PARAM, inquiryId);
  params.set(RETURN_TO_QUERY_PARAM, RETURN_TO_BOOKINGS);
  params.set(FOCUS_QUERY_PARAM, inquiryId);
  return `${base}?${params.toString()}`;
}

/**
 * Find a guest inquiry in my-inquiries list; optional listing id must match.
 */
export function resolveInquiryFromList(inquiries, inquiryId, listingId) {
  if (!inquiryId || !Array.isArray(inquiries)) {
    return null;
  }
  const match = inquiries.find(
    (item) => item?.id === inquiryId || item?.inquiry_id === inquiryId,
  );
  if (!match) {
    return null;
  }
  if (listingId && match.listing_id && match.listing_id !== listingId) {
    return null;
  }
  return match;
}

/**
 * Strip inquiry to safe fields for listing detail banner (no private text).
 */
export function pickSafeInquiryContextForBanner(inquiry) {
  if (!inquiry || typeof inquiry !== 'object') {
    return null;
  }
  const safe = {
    id: inquiry.id,
    inquiry_ref: inquiry.inquiry_ref || shortId(inquiry.id),
    listing_ref: inquiry.listing_ref || shortId(inquiry.listing_id),
    listing_id: inquiry.listing_id,
    check_in_date: inquiry.check_in_date,
    check_out_date: inquiry.check_out_date,
    guests: inquiry.guests,
    status: inquiry.status,
    viewing_state: inquiry.viewing_state,
    created_at: inquiry.created_at,
    nights: inquiry.nights,
  };
  FORBIDDEN_CONTEXT_KEYS.forEach((key) => {
    if (key in safe) {
      delete safe[key];
    }
  });
  return safe;
}

export function getInquiryStatusLabel(status, t) {
  const key = `booking.inquiry_status_${String(status || 'pending').toLowerCase()}`;
  const defaults = {
    pending: 'Awaiting owner response',
    approved: 'Approved',
    declined: 'Declined',
    confirmed: 'Confirmed',
    cancelled: 'Withdrawn',
    refunded: 'Refunded',
    closed: 'Closed',
  };
  const normalized = String(status || 'pending').toLowerCase();
  return t(key, defaults[normalized] || defaults.pending);
}

export function parseInquiryIdFromSearchParams(searchParams) {
  if (!searchParams) {
    return null;
  }
  const raw = searchParams.get(INQUIRY_QUERY_PARAM);
  if (!raw || !String(raw).trim()) {
    return null;
  }
  return String(raw).trim();
}

export function parseFocusInquiryIdFromSearchParams(searchParams) {
  if (!searchParams) {
    return null;
  }
  const raw = searchParams.get(FOCUS_QUERY_PARAM);
  if (!raw || !String(raw).trim()) {
    return null;
  }
  return String(raw).trim();
}
